import React, {useEffect, useMemo, useState} from "react";
import axios, {AxiosError} from "axios";
import {ColumnDef} from "@tanstack/react-table";
import {Link, useNavigate, useParams} from "react-router-dom";

import {DataGrid, KeenIcon} from "@/components";
import {Loading} from '@/layouts/buddy/loading';
import {enqueueSnackbar} from "notistack";
import {CandidateDetailsData} from "@/pages/buddy/types/CandidateDetailsData";
import {getProgressColor} from "@/pages/buddy/utils/ProgressColor.tsx";
import {MatchDetailsData} from "@/pages/buddy/types/MatchDetailsData.ts";
import {formatDate} from "@/pages/buddy/utils/Date.tsx";

const Candidates = () => {
    const storageFilterId = "candidates-filter";
    const {p_id: job_p_id} = useParams(); // Access `job_p_id` from URL
    const [data, setData] = useState<CandidateDetailsData[]>([]);
    const [loading, setLoading] = useState(true);
    const [isActiveFilter, setIsActiveFilter] = useState(true);

    const API_CANDIDATES = `${import.meta.env.VITE_APP_API_URL}/candidates`;
    const API_MATCH = `${import.meta.env.VITE_APP_API_URL}/match`;

    const navigate = useNavigate();

    // Fetch candidates data
    const fetchCandidatesData = async () => {
        try {
            const response = await axios.get<CandidateDetailsData[]>(`${API_CANDIDATES}/${job_p_id}`, {
                headers: {Accept: "application/json"},
            });
            setData(response.data);
            setLoading(false);
        } catch (err) {
            enqueueSnackbar(`Failed to fetch candidates data: ${err}`, {
                variant: 'solid',
                state: 'danger'
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCandidatesData();
    }, []);

    // Generate match function
    const generateMatch = async (candidate_p_id: string) => {
        setLoading(true);

        try {
            // Retrieve auth token from local storage
            const token = localStorage.getItem("authToken");

            // Send the POST request to create a match
            const response = await axios.post(
                `${API_MATCH}/${job_p_id}/${candidate_p_id}`,
                {}, // No body payload needed
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            // If successful, navigate to the candidate's page
            if (response.status === 200) {
                navigate(`/candidate/${candidate_p_id}`);
            }
        } catch (err) {
            // Handle errors with type guards
            if (err instanceof AxiosError && err.response) {
                enqueueSnackbar(`Failed to generate match: ${err}`, {
                    variant: 'solid',
                    state: 'danger'
                });
            } else if (err instanceof Error) {
                enqueueSnackbar(`Failed to fetch candidates data: ${err}`, {
                    variant: 'solid',
                    state: 'danger'
                });
            } else {
                enqueueSnackbar(`An unknown error occurred: ${err}`, {
                    variant: 'solid',
                    state: 'danger'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo<ColumnDef<CandidateDetailsData>[]>(() => [
        {
            accessorFn: (row) => row.first_name,
            id: 'name',
            header: () => 'Candidate',
            enableSorting: true,
            cell: (info) => (
                <div className="flex flex-col">
                    <Link
                        className="leading-none font-medium text-sm text-gray-900 hover:text-primary"
                        to={`/candidate/${info.row.original.p_id}`}
                    >
                        {info.row.original.first_name} {info.row.original.last_name}
                    </Link>
                </div>
            ),
        },
        {
            accessorFn: (row) => row.matches?.[0] as MatchDetailsData | undefined,
            id: 'score',
            header: () => 'Match',
            enableSorting: true,
            cell: (info) => {
                const match = info.row.original.matches?.[0];
                if (!match) return 'No Matches';

                const value = match.overall_soft_score_relative;
                const percentage = value !== undefined && value !== null ? Math.round(value * 100) : null;

                return (
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2">
                            <button
                                className="btn btn-sm btn-icon btn-clear btn-light"
                                onClick={() => navigate(`/match/${match.p_id}`)}
                            >
                                <KeenIcon icon="eye"/>
                            </button>
                            <Link
                                className="leading-none hover:text-primary"
                                to={`/match/${match.p_id}`}
                            >
                                {percentage}%
                            </Link>
                        </div>
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                            <div
                                className="h-1.5 rounded-full"
                                style={{width: `${percentage}%`, backgroundColor: getProgressColor(percentage)}}
                            ></div>
                        </div>
                    </div>
                );
            },
            meta: {
                className: 'w-[250px]',
                cellClassName: 'text-gray-700 font-normal',
            },
        },
        {
            id: 'all-candidates',
            header: () => 'Matched Jobs',
            enableSorting: false,
            cell: ({row}) => (
                <div className="flex text-sm font-normal items-center gap-2">
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => navigate(`/candidate/${row.original.p_id}/jobs`)}
                    >
                        <KeenIcon icon="eye"/>
                    </button>
                    <Link
                        className="leading-none hover:text-primary"
                        to={`/candidate/${row.original.p_id}/jobs`}
                    >
                        {row.original.matches ? row.original.matches.length : 0} Jobs
                    </Link>
                    <span></span>
                </div>
            ),
        },
        {
            accessorFn: (row) => row.updated_at,
            id: 'lastModified',
            enableSorting: true,
            header: () => 'Last Modified',
            cell: (info) => {
                const value = info.getValue() as string | null; // Explicitly cast the value
                return formatDate(value);
            },
            meta: {
                className: 'w-[350px]',
                cellClassName: 'text-gray-700 font-normal',
            },
        },
        {
            id: 'edit',
            header: () => 'Open',
            enableSorting: false,
            cell: ({row}) => (
                <button
                    className="btn btn-sm btn-icon btn-clear btn-light"
                    onClick={() => navigate(`/candidate/${row.original.p_id}`)}
                >
                    <KeenIcon icon="notepad-edit"/>
                </button>
            ),
            meta: {
                className: 'w-[60px]',
            },
        },
        {
            id: 'delete',
            header: () => 'Delete',
            enableSorting: false,
            cell: ({row}) => (
                <button
                    className="btn btn-sm btn-icon btn-clear btn-light"
                    onClick={() => alert(`Clicked on delete for ${row.original.first_name}`)}
                >
                    <KeenIcon icon="trash"/>
                </button>
            ),
            meta: {
                className: 'w-[60px]',
            },
        },
    ], [job_p_id]);

    // Initialize search term from localStorage if available
    const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem(storageFilterId) || "");

    useEffect(() => {
        localStorage.setItem(storageFilterId, searchTerm);
    }, [searchTerm]);

    const filteredData = useMemo(() => {
        const searchLower = searchTerm.toLowerCase();

        return data.filter((candidate) => {
            const matchesSearch = (candidate.last_name?.toLowerCase() || "").includes(searchLower);
            const matchesActiveFilter = !isActiveFilter || candidate.is_active;

            return matchesSearch && matchesActiveFilter;
        });
    }, [searchTerm, isActiveFilter, data]);

    if (loading) return <Loading loading={loading}/>;

    return (
        <div className="card card-grid min-w-full">
            <div className="card-header flex-wrap py-5">
                <h3 className="card-title">Candidates</h3>
                <div className="flex gap-6">
                    <div className="relative">
                        <KeenIcon
                            icon="magnifier"
                            className="leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"
                        />
                        <input
                            type="text"
                            placeholder="Search Candidates"
                            className="input input-sm pl-8"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <label className="switch switch-sm">
                        <input
                            name="check"
                            type="checkbox"
                            checked={isActiveFilter}
                            onChange={() => setIsActiveFilter(!isActiveFilter)}
                            className="order-2"
                        />
                        <span className="switch-label order-1">Only Active Candidates</span>
                    </label>
                </div>
            </div>

            <div className="card-body">
                <DataGrid
                    key={`${searchTerm}-${isActiveFilter}`}
                    columns={columns}
                    data={filteredData}
                    pagination={{size: 10}}
                    sorting={[{id: "name", desc: false}]}
                />
            </div>
        </div>
    );
};

export {Candidates};
